import { Component } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { logger } from "../utils/logger";
import { Global } from "../constants/global";

const ErrorDisplay = ({ error, errorInfo, onReset, onReload }) => {
  const theme = useTheme();
  const backgroundColor = theme.palette.background.default || "#f8fafc";
  const textColor = theme.palette.text.primary || "#0f172a";
  const secondaryColor = theme.palette.text.secondary || "#64748b";
  const primaryMain = theme.palette.primary.main || "#2563eb";
  const borderColor = theme.palette.divider || "rgba(15, 23, 42, 0.08)";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 4,
        textAlign: "center",
        backgroundColor: backgroundColor,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          backgroundColor: "rgba(37, 99, 235, 0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 4,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: primaryMain,
          }}
        />
      </Box>

      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          color: textColor,
          mb: 2,
          letterSpacing: "-0.02em",
        }}
      >
        Something went wrong
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: secondaryColor,
          mb: 5,
          maxWidth: 480,
          lineHeight: 1.6,
          fontSize: "1rem",
        }}
      >
        We encountered an unexpected error while processing your request. Please
        try refreshing the page or return to the enterprise dashboard.
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={onReload}
          sx={{
            fontWeight: 600,
            backgroundColor: primaryMain,
            color: "#FFFFFF",
            px: 4,
            py: 1.5,
            borderRadius: 2,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
            },
          }}
        >
          Refresh Page
        </Button>

        <Button
          variant="outlined"
          onClick={onReset}
          sx={{
            borderColor: "rgba(15, 23, 42, 0.2)",
            color: textColor,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            "&:hover": {
              backgroundColor: "rgba(15, 23, 42, 0.02)",
              borderColor: "rgba(15, 23, 42, 0.3)",
            },
          }}
        >
          Return to Dashboard
        </Button>
      </Box>

      {process.env.VITE_ENV !== Global.PRD && error && (
        <Box
          sx={{
            mt: 6,
            p: 3,
            backgroundColor: "#FFFFFF",
            borderRadius: 3,
            border: `1px solid ${borderColor}`,
            maxWidth: 800,
            width: "100%",
            textAlign: "left",
            boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontWeight: 700,
              color: "#ef4444",
              display: "block",
              mb: 1,
            }}
          >
            Developer Diagnostics
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.75rem",
              whiteSpace: "pre-wrap",
              color: textColor,
              opacity: 0.8,
            }}
          >
            {error.toString()}
            {"\n\nStack Trace Summary:"}
            {errorInfo?.componentStack?.split("\n").slice(0, 5).join("\n")}
            {"\n..."}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    logger.error("ErrorBoundary caught an error:", {
      error: error.toString(),
      stack: errorInfo.componentStack,
      url: window.location.href,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = "/dashboard";
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
